// app/(tabs)/market/boutiques/[slug].tsx
//
// Page publique complète d'une boutique : header (logo, tagline,
// description, contacts selon ui_config.header), bouton s'abonner/
// se désabonner, liste de ses produits, rendu thémé selon ui_config
// (couleurs) via ThemeProvider. Clic sur un produit → navigue vers
// l'écran dédié produits/[id].tsx (même mécanisme que index.tsx),
// pas de bascule interne à cet écran.
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.boutiqueDetail / market.common).

import React, { useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

import { useShop } from '@/hooks/market/useShop';
import { useShopProducts } from '@/hooks/market/useShopProducts';
import { useSubscriptions } from '@/hooks/market/useSubscriptions';
import ShopHeader from '@/components/market/ShopHeader';
import MarketThemeProvider, { useMarketTheme } from '@/components/market/ThemeProvider';
import type { MarketFeedProduct } from '@/hooks/market/useMarketFeed';

const NATIVE = Platform.OS !== 'web';

function formatPrice(amount: number, currency: string) {
  return `${amount.toLocaleString('fr-FR')} ${currency}`;
}

// Carte produit compacte, stylée avec la couleur primaire de la
// boutique (via useMarketTheme) — distincte de la carte plus riche du
// feed général (index.tsx) qui n'a pas de thème à respecter.
function ThemedProductCard({ product, onPress }: { product: MarketFeedProduct; onPress: () => void }) {
  const { t } = useTranslation();
  const { config } = useMarketTheme();

  return (
    <TouchableOpacity style={styles.productCard} activeOpacity={0.7} onPress={onPress}>
      <View style={styles.productImagePlaceholder}>
        <Ionicons name="image-outline" size={24} color="#C4B5FD" />
        {product.is_featured && (
          <View style={[styles.featuredBadge, { backgroundColor: config.colors.primary }]}>
            <Text style={styles.featuredBadgeText}>{t('market.boutiqueDetail.newBadge')}</Text>
          </View>
        )}
      </View>
      <Text style={styles.productName} numberOfLines={1}>{product.name}</Text>
      <View style={styles.priceRow}>
        {product.promo_price ? (
          <>
            <Text style={[styles.promoPrice, { color: config.colors.primary }]}>
              {formatPrice(product.promo_price, product.currency_code)}
            </Text>
            <Text style={styles.originalPrice}>{formatPrice(product.price, product.currency_code)}</Text>
          </>
        ) : (
          <Text style={[styles.price, { color: config.colors.primary }]}>
            {formatPrice(product.price, product.currency_code)}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
}

function ShopDetailContent({ slug }: { slug: string }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { shop, loading: shopLoading, error: shopError, notFound } = useShop(slug);
  const { products, loading: productsLoading, loadingMore, error: productsError, hasMore, loadMore } = useShopProducts(slug);
  const { isSubscribed, toggleSubscribe, togglingId } = useSubscriptions();

  const handleProductPress = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/produits/${product.product_id}`);
  }, [router]);

  const handleToggleSubscribe = useCallback(() => {
    if (!shop) return;
    toggleSubscribe(shop.shop_id);
  }, [shop, toggleSubscribe]);

  const handleScrollExtra = useCallback((e: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const ratio = (contentOffset.y + layoutMeasurement.height) / contentSize.height;
    if (ratio > 0.85 && hasMore && !loadingMore) loadMore();
  }, [hasMore, loadingMore, loadMore]);

  if (shopLoading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="small" color="#6610B1" />
        <Text style={styles.centerText}>{t('market.boutiqueDetail.loading')}</Text>
      </View>
    );
  }

  if (notFound) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="alert-circle-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>{t('market.boutiqueDetail.notFound')}</Text>
      </View>
    );
  }

  if (shopError || !shop) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="cloud-offline-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>
          {shopError === 'session_expired'
            ? t('market.common.sessionExpiredReconnect')
            : t('market.boutiqueDetail.cannotLoad')}
        </Text>
      </View>
    );
  }

  return (
    <MarketThemeProvider config={shop.ui_config}>
      <ScrollView
        style={styles.container}
        onScroll={handleScrollExtra}
        scrollEventThrottle={16}
      >
        <ShopHeader
          shop={shop}
          isSubscribed={isSubscribed(shop.shop_id)}
          onToggleSubscribe={handleToggleSubscribe}
          togglingSubscribe={togglingId === shop.shop_id}
        />

        <View style={styles.productsSection}>
          <Text style={styles.productsSectionTitle}>{t('market.boutiqueDetail.productsTitle')}</Text>

          {productsLoading && (
            <View style={styles.centerBox}>
              <ActivityIndicator size="small" color="#6610B1" />
            </View>
          )}

          {!productsLoading && productsError && (
            <View style={styles.centerBox}>
              <Text style={styles.centerBoxText}>{t('market.boutiqueDetail.cannotLoadProducts')}</Text>
            </View>
          )}

          {!productsLoading && !productsError && products.length === 0 && (
            <View style={styles.centerBox}>
              <Text style={styles.centerBoxText}>{t('market.boutiqueDetail.noProducts')}</Text>
            </View>
          )}

          <View style={styles.productsGrid}>
            {products.map((product) => (
              <ThemedProductCard
                key={product.product_id}
                product={product}
                onPress={() => handleProductPress(product)}
              />
            ))}
          </View>

          {loadingMore && (
            <View style={styles.centerBox}>
              <ActivityIndicator size="small" color="#6610B1" />
            </View>
          )}
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </MarketThemeProvider>
  );
}

export default function ShopDetailScreen() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { t } = useTranslation();

  if (!slug) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.centerText}>{t('market.boutiqueDetail.shopNotFound')}</Text>
      </View>
    );
  }

  return <ShopDetailContent slug={slug} />;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0F2F5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
    backgroundColor: '#fff',
  },
  centerText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  productsSection: {
    padding: 16,
  },
  productsSectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: '#222',
    marginBottom: 12,
  },
  centerBox: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  centerBoxText: {
    fontSize: 13,
    color: '#999',
  },
  productsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  productCard: {
    width: '48%',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  productImagePlaceholder: {
    width: '100%',
    height: 100,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    borderRadius: 6,
    paddingHorizontal: 5,
    paddingVertical: 2,
  },
  featuredBadgeText: {
    fontSize: 8,
    fontWeight: '700',
    color: '#fff',
  },
  productName: {
    fontSize: 12.5,
    fontWeight: '700',
    color: '#222',
    marginTop: 8,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 5,
    marginTop: 3,
  },
  price: {
    fontSize: 13,
    fontWeight: '700',
  },
  promoPrice: {
    fontSize: 13,
    fontWeight: '700',
  },
  originalPrice: {
    fontSize: 11,
    color: '#999',
    textDecorationLine: 'line-through',
  },
});
